import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { playerAPI } from '../services/api';
import { trackCompleteRegistration, trackEvent } from '../utils/metaPixel';
import { getAttributionForSubmission } from '../utils/attributionTracker';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: ${(p) => (p.$warn ? '#d97706' : '#10b981')};
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  background-color: #043663;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  margin: 0.25rem;

  &:hover {
    background: #032a4a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  margin: 0 0 1.25rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.9rem;
  line-height: 1.45;
  text-align: left;
`;

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPlayerFlow = location.pathname.startsWith('/player/');
  const teamId = searchParams.get('team_id');
  const seasonId = searchParams.get('season_id');
  const sessionId = searchParams.get('session_id');
  const invoiceId = searchParams.get('invoice_id');
  const isPreview = searchParams.get('preview') === 'true';
  const [resolvedTeamId, setResolvedTeamId] = useState(null);
  const [isVerifying, setIsVerifying] = useState(!!sessionId && !isPreview);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [showVerificationError, setShowVerificationError] = useState(false);

  useEffect(() => {
    console.log('=== PAYMENT SUCCESS PAGE LOADED ===');
    console.log('URL:', window.location.href);
    console.log('Team ID:', teamId);
    console.log('Session ID:', sessionId);
    console.log('Invoice ID:', invoiceId);
    console.log('=== END PAGE LOAD DEBUG ===');
  }, [teamId, sessionId, invoiceId, isPreview]);

  useEffect(() => {
    const verifyPayment = async () => {
      if (isPreview || sessionId?.startsWith('test_')) {
        setVerificationStatus('Test mode - Payment simulation successful!');
        setShowVerificationError(false);
        setIsVerifying(false);
        setTimeout(() => {
          trackEvent('Purchase', {
            value: 160,
            currency: 'CHF',
            content_name: 'Team Registration Fee (TEST)',
            content_category: 'Payment',
            content_ids: [teamId || 'test_team_123'],
            num_items: 1,
          });
        }, 1000);
        return;
      }

      if (!sessionId) {
        setIsVerifying(false);
        return;
      }

      setIsVerifying(true);
      setShowVerificationError(false);
      try {
        if (invoiceId) {
          const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
          let lastErr = null;
          let invRes = null;
          // Webhook + verify often race; auto-retry so players don't need "Retry setup"
          for (let attempt = 1; attempt <= 5; attempt++) {
            try {
              invRes = await playerAPI.verifyRegistrationInvoice(invoiceId, sessionId);
              if (invRes?.success !== false && invRes?.data?.teamId) {
                setVerificationStatus('Payment verified successfully!');
                setShowVerificationError(false);
                setResolvedTeamId(invRes.data.teamId);
                setTimeout(() => {
                  const attribution = getAttributionForSubmission();
                  const serverEventId = invRes.data.meta_event_id;
                  trackCompleteRegistration(
                    {
                      id: invRes.data.teamId,
                      promo_code: invRes.data.promo_code,
                    },
                    attribution,
                    serverEventId
                  );
                }, 1000);
                return;
              }
              lastErr = new Error(invRes?.message || 'Verification failed');
            } catch (err) {
              lastErr = err;
              console.warn(`Invoice verify attempt ${attempt} failed`, err?.response?.data || err);
            }
            if (attempt < 5) await sleep(800 * attempt);
          }
          setShowVerificationError(true);
          setVerificationStatus(
            lastErr?.response?.data?.message ||
              lastErr?.message ||
              invRes?.message ||
              'Payment was received but team setup failed. Please retry or contact support.'
          );
          return;
        }

        const response = await api.post('/public-payments/verify-payment', {
          session_id: sessionId,
        });

        if (response.success) {
          setVerificationStatus('Payment verified successfully!');
          setShowVerificationError(false);
          if (response.team_id) setResolvedTeamId(response.team_id);

          setTimeout(() => {
            const finalTeamId = response.team_id || teamId;
            const attribution = getAttributionForSubmission();
            const serverEventId = response.meta_event_id;
            trackCompleteRegistration(
              { id: finalTeamId, promo_code: response.promo_code },
              attribution,
              serverEventId
            );
          }, 1000);

          if (response.team_id && !teamId) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('team_id', response.team_id);
            window.history.replaceState({}, '', newUrl);
          }
        } else {
          setShowVerificationError(true);
          setVerificationStatus(
            response?.message ||
              'Payment verification failed. If you were charged, contact support with your session id.'
          );
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setShowVerificationError(true);
        setVerificationStatus(
          error?.response?.data?.message ||
            error?.message ||
            'Payment was received but team setup failed. Please retry or contact support.'
        );
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId, teamId, invoiceId, isPreview]);

  useEffect(() => {
    if (window.opener && !isPreview && !showVerificationError) {
      window.opener.postMessage({ type: 'PAYMENT_SUCCESS', teamId }, window.location.origin);
      setTimeout(() => {
        window.close();
      }, 3000);
    }
  }, [teamId, isPreview, showVerificationError]);

  useEffect(() => {
    if (isPreview || window.opener || showVerificationError || isVerifying) return;
    const tid = resolvedTeamId || teamId;
    if (isPlayerFlow && sessionId && !tid) return;
    const delayMs = isPlayerFlow ? 4000 : 5000;
    const timer = setTimeout(() => {
      if (isPlayerFlow && tid) {
        navigate('/player/teams');
        return;
      }
      if (!isPlayerFlow) {
        navigate('/login');
      } else {
        navigate('/player');
      }
    }, delayMs);

    return () => clearTimeout(timer);
  }, [
    navigate,
    isPreview,
    isPlayerFlow,
    resolvedTeamId,
    teamId,
    seasonId,
    sessionId,
    showVerificationError,
    isVerifying,
  ]);

  const retryInvoiceVerify = () => {
    if (!invoiceId || !sessionId) return;
    setIsVerifying(true);
    setShowVerificationError(false);
    playerAPI
      .verifyRegistrationInvoice(invoiceId, sessionId)
      .then((invRes) => {
        if (invRes?.success !== false && invRes?.data?.teamId) {
          setResolvedTeamId(invRes.data.teamId);
          setVerificationStatus('Payment verified successfully!');
          setShowVerificationError(false);
          setTimeout(() => {
            const attribution = getAttributionForSubmission();
            trackCompleteRegistration(
              {
                id: invRes.data.teamId,
                promo_code: invRes.data.promo_code,
              },
              attribution,
              invRes.data.meta_event_id
            );
          }, 500);
        } else {
          setShowVerificationError(true);
          setVerificationStatus(invRes?.message || 'Verification failed');
        }
      })
      .catch((error) => {
        setShowVerificationError(true);
        setVerificationStatus(
          error?.response?.data?.message || error?.message || 'Verification failed'
        );
      })
      .finally(() => setIsVerifying(false));
  };

  return (
    <PageContainer>
      <Card>
        <SuccessIcon $warn={showVerificationError}>
          {showVerificationError ? '⚠️' : '✅'}
        </SuccessIcon>
        <Title>
          {showVerificationError
            ? t(
                'paymentFlows.paymentSuccessPage.titleError',
                'Payment received — setup incomplete'
              )
            : t('paymentFlows.paymentSuccessPage.title')}
        </Title>
        {showVerificationError && verificationStatus ? (
          <ErrorBox>
            {verificationStatus}
            {sessionId ? (
              <>
                <br />
                <br />
                <strong>Session:</strong> {sessionId}
              </>
            ) : null}
            {invoiceId ? (
              <>
                <br />
                <strong>Invoice:</strong> {invoiceId}
              </>
            ) : null}
          </ErrorBox>
        ) : null}
        <Message>
          {isVerifying ? (
            t('paymentFlows.paymentSuccessPage.verifying')
          ) : showVerificationError ? (
            t(
              'paymentFlows.paymentSuccessPage.bodyError',
              'Your card may have been charged, but we could not finish creating your team. Retry setup below, or contact support with the details above.'
            )
          ) : (
            <>
              {t('paymentFlows.paymentSuccessPage.bodyDone')}
              <br />
              <br />
              <strong>{t('paymentFlows.paymentSuccessPage.welcomeBold')}</strong>
              <br />
              <br />
              {window.opener && !isPreview
                ? t('paymentFlows.paymentSuccessPage.hintOpener')
                : isPlayerFlow
                  ? t('paymentFlows.paymentSuccessPage.hintPlayerFlow')
                  : t('paymentFlows.paymentSuccessPage.hintClassic')}
            </>
          )}
        </Message>
        {showVerificationError && invoiceId ? (
          <Button type="button" disabled={isVerifying} onClick={retryInvoiceVerify}>
            {t('paymentFlows.paymentSuccessPage.retryVerify', 'Retry setup')}
          </Button>
        ) : null}
        <Button
          type="button"
          disabled={isVerifying}
          onClick={() => {
            if (isPlayerFlow) {
              navigate('/player/teams');
            } else {
              navigate('/login');
            }
          }}
        >
          {isPlayerFlow
            ? t('paymentFlows.paymentSuccessPage.goToMyTeams')
            : t('paymentFlows.paymentSuccessPage.goToLogin')}
        </Button>
      </Card>
    </PageContainer>
  );
};

export default PaymentSuccessPage;
